import type { Vec2, Vec3, Ring } from '../../geo/types';
import type { BakedCrowd } from './crowd/crowdPlan';
import type { DraftRoute } from './draftPlan';

export const CITIZEN_ROLES = [
  'friar',
  'sister',
  'burgomaster',
  'gentleperson',
  'sailor',
  'shipmaster',
  'trader',
  'baker',
  'butcher',
  'craftsman',
  'watchman',
  'schout',
  'burglar',
  'carer',
  'physician',
] as const;
export type CitizenRole = (typeof CITIZEN_ROLES)[number];
export type LifeRole = CitizenRole | 'horse' | 'dog';

export type LifeAction = 'walk' | 'wait' | 'work' | 'inside';
export type LifeProp = 'basket' | 'parcel' | 'plank' | 'tool' | 'lantern';
export interface LifeStep {
  readonly from: Vec2;
  readonly to: Vec2;
  readonly seconds: number;
  readonly action: LifeAction;
  readonly heading: number;
  readonly prop?: LifeProp;
  readonly door?: string;
}
export interface LifeEntrance {
  readonly id: string;
  readonly lotKey: string;
  readonly position: Vec2;
  readonly outward: Vec2;
  readonly right: Vec2;
  readonly width: number;
  readonly height: number;
  readonly groundY: number;
  readonly stepReach: number;
  readonly stepHeight: number;
  readonly streetId: string;
  readonly kind: 'home' | 'bakery' | 'shop';
}
/** The glazed upper lights of one front window with a candle behind them after dark. */
export interface LitWindow {
  /** World metres: the middle of the glazed lights, just outside the glass. */
  readonly position: Vec3;
  readonly outward: Vec3;
  readonly width: number;
  readonly height: number;
  /** The share of the evening's lights that must still burn for this one to (burningShare). */
  readonly from: number;
}
export interface LifePlan {
  readonly routes: readonly LifeRoute[];
  readonly entrances: readonly LifeEntrance[];
  readonly windows?: readonly LitWindow[];
  /** The crowd's net, doors and places (crowd/crowdPlan.ts). */
  readonly crowd?: BakedCrowd;
  /** Loaded-cart itineraries retained so startup need not repeat street clearance queries. */
  readonly cargoRoutes?: readonly (readonly Vec2[])[];
  readonly draftRoutes?: readonly DraftRoute[];
  /** Only solid envelopes near these itineraries; baked so startup need not rebuild landmarks. */
  readonly clearanceRings?: readonly Ring[];
}

export interface LifeRoute {
  readonly id: string;
  readonly role: LifeRole;
  readonly streetId: string;
  readonly a: Vec2;
  readonly b: Vec2;
  readonly radius: number;
  readonly speed: number;
  readonly scale: number;
  readonly phase: number;
  readonly appearance?: 'woman';
  readonly routine?: readonly LifeStep[];
  readonly daytime?: boolean;
  /** Out after dark only: the watch, and the few residents with an errand, each with a lantern. */
  readonly night?: boolean;
  /** Of the night's cast, those on watch: out from the muster at nine in the evening only. */
  readonly watch?: boolean;
  /** Of the night's cast, those abroad in the dead of night only (nightBurglars.ts). */
  readonly late?: boolean;
}

export interface LifePose {
  readonly position: Vec2;
  readonly heading: number;
  readonly stride: number;
  readonly gait: number;
  readonly gaitWeight: number;
  readonly walking: boolean;
  readonly visible?: boolean;
  readonly action?: LifeAction;
  readonly prop?: LifeProp;
  readonly door?: string;
  readonly doorOpen?: boolean;
}
